import { createGlobalState } from '@vueuse/core';
import { ref, nextTick } from 'vue';
import { useToasted } from '~/composables/useToasted';
import { HTTP_INTERNAL_SERVER_ERROR, HTTP_NOT_FOUND } from '~/support/HttpStatuses';
import {
    adminTeamsClient,
    adminUsersClient,
    invitationsClient,
    groupsClient,
    orgUnitsClient,
    phoneNumbersClient,
    teamCustomAttributesClient,
    usersClient,
} from '~/services';
import { isAxiosError } from 'axios';
import type { KioskTeam } from '~/services/adminTeamsClient.types';

export const useTeamDetails = createGlobalState(() => {
    const toasted = useToasted();

    const team = ref<KioskTeam>({});
    const loading = ref(true);

    const catchRequestError = (error: unknown) => {
        console.error(error);
        if (
            isAxiosError<{ message?: string }>(error) &&
            error.response?.status &&
            error.response.status < HTTP_INTERNAL_SERVER_ERROR &&
            error.response.status != HTTP_NOT_FOUND
        ) {
            toasted?.error(error.response.data?.message || 'Something went wrong.');
        } else {
            toasted?.global.platform_error();
        }
    };

    let onMountedTeamCallback: () => void = () => {};
    const onMountedTeam = (callback: () => void) => {
        onMountedTeamCallback = callback;
    };

    const mount = async (teamId: number) => {
        await nextTick();

        if (!teamId) {
            team.value = {};
            loading.value = false;
            onMountedTeamCallback?.();
            return;
        }

        try {
            team.value.id = teamId;
            loading.value = true;

            const response = await adminTeamsClient.getTeam(teamId);
            team.value = response.data;
            loading.value = false;
            onMountedTeamCallback?.();
        } catch (e) {
            catchRequestError(e);
        }
    };

    const unmount = () => {
        team.value = {};
        loading.value = false;
    };

    const refreshTeam = async (teamId: number) => {
        try {
            const response = await adminTeamsClient.getTeam(teamId);
            team.value = response.data;
        } catch (e) {
            console.error(e);
        }
    };

    const parent = ref<KioskTeam | undefined>(undefined);
    const parentLoading = ref(false);
    const getParent = async () => {
        if (!team.value.parent_id) {
            return;
        }

        try {
            parentLoading.value = true;
            const response = await adminTeamsClient.getTeam(team.value.parent_id);
            parent.value = response.data;
        } catch (e) {
            catchRequestError(e);
        } finally {
            parentLoading.value = false;
        }
    };

    const owner = ref<Record<string, unknown> | undefined>(undefined);
    const ownerLoading = ref(false);
    const getOwner = async () => {
        try {
            ownerLoading.value = true;
            const response = await adminUsersClient.getUser(team.value.owner_id);
            owner.value = response.data;
        } catch (e) {
            catchRequestError(e);
        } finally {
            ownerLoading.value = false;
        }
    };

    const invitationsCount = ref<number | undefined>(undefined);
    const invitationsCountLoading = ref(false);
    const getInvitationsCount = async () => {
        try {
            invitationsCountLoading.value = true;
            const response = await invitationsClient.invitesCount(team.value.id);
            invitationsCount.value = response.data.count;
        } catch (e) {
            catchRequestError(e);
        } finally {
            invitationsCountLoading.value = false;
        }
    };

    const usersCount = ref<number | undefined>(undefined);
    const usersCountLoading = ref(false);
    const getUsersCount = async () => {
        try {
            usersCountLoading.value = true;
            const response = await usersClient.usersCount(team.value.id);
            usersCount.value = response.data.count;
        } catch (e) {
            catchRequestError(e);
        } finally {
            usersCountLoading.value = false;
        }
    };

    const teamsCount = ref<number | undefined>(undefined);
    const teamsCountLoading = ref(false);
    const getTeamsCount = async () => {
        try {
            teamsCountLoading.value = true;
            const response = await adminTeamsClient.countSubTeams(team.value.id!);
            teamsCount.value = response.data.count;
        } catch (e) {
            catchRequestError(e);
        } finally {
            teamsCountLoading.value = false;
        }
    };

    const orgUnitsCount = ref<number | undefined>(undefined);
    const orgUnitsCountLoading = ref(false);
    const getOrgUnitsCount = async () => {
        try {
            orgUnitsCountLoading.value = true;
            const response = await orgUnitsClient.countOrgUnits(team.value.id);
            orgUnitsCount.value = response.data.count;
        } catch (e) {
            catchRequestError(e);
        } finally {
            orgUnitsCountLoading.value = false;
        }
    };

    const groupsCount = ref<number | undefined>(undefined);
    const groupsCountLoading = ref(false);
    const getGroupsCount = async () => {
        try {
            groupsCountLoading.value = true;
            const response = await groupsClient.countGroups({ teamId: team.value.id! });
            groupsCount.value = response.data.count;
        } catch (e) {
            catchRequestError(e);
        } finally {
            groupsCountLoading.value = false;
        }
    };

    const phoneNumbersCount = ref<number | undefined>(undefined);
    const phoneNumbersCountLoading = ref(false);
    const getPhoneNumbersCount = async () => {
        try {
            phoneNumbersCountLoading.value = true;
            const response = await phoneNumbersClient.countPhoneNumbers(team.value.id);
            phoneNumbersCount.value = response.data.count;
        } catch (e) {
            catchRequestError(e);
        } finally {
            phoneNumbersCountLoading.value = false;
        }
    };

    const phoneNumbers = ref<unknown[] | undefined>(undefined);
    const phoneNumbersLoading = ref(false);
    const getPhoneNumbers = async (params: Record<string, unknown>) => {
        try {
            phoneNumbersLoading.value = true;
            const response = await phoneNumbersClient.listPhoneNumbers(team.value.id, params);
            phoneNumbers.value = response.data.data;
        } catch (e) {
            catchRequestError(e);
        } finally {
            phoneNumbersLoading.value = false;
        }
    };

    const customAttributes = ref<unknown[] | undefined>(undefined);
    const customAttributesLoading = ref(false);
    const getCustomAttributes = async () => {
        try {
            customAttributesLoading.value = true;
            const response = await teamCustomAttributesClient.getList(team.value.id);
            customAttributes.value = response.data.data;
        } catch (e) {
            catchRequestError(e);
        } finally {
            customAttributesLoading.value = false;
        }
    };

    return {
        loading,
        team,
        mount,
        unmount,
        refreshTeam,
        onMountedTeam,

        owner,
        ownerLoading,
        getOwner,

        parent,
        parentLoading,
        getParent,

        invitationsCount,
        invitationsCountLoading,
        getInvitationsCount,

        usersCount,
        usersCountLoading,
        getUsersCount,

        teamsCount,
        teamsCountLoading,
        getTeamsCount,

        orgUnitsCount,
        orgUnitsCountLoading,
        getOrgUnitsCount,

        groupsCount,
        groupsCountLoading,
        getGroupsCount,

        phoneNumbersCount,
        phoneNumbersCountLoading,
        getPhoneNumbersCount,

        phoneNumbers,
        phoneNumbersLoading,
        getPhoneNumbers,

        customAttributes,
        customAttributesLoading,
        getCustomAttributes,
    };
});
